import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronRight, Lock, FlaskConical, Clock, Layers, RotateCcw } from 'lucide-react';
import { Link } from 'react-router-dom';
import ProgressRing from './ProgressRing';
import StatusBadge from './StatusBadge';
import MaserasiTimer from './MaserasiTimer';
import { formatRelativeTime } from '@/lib/timeUtils';
import LastUpdated from '@/components/ui/LastUpdated';


const CORE_CATEGORIES = ['botol', 'bibit', 'tutup', 'spray', 'bahan_cair', 'tambahan'];

export default function BrandCard({ order, stockMap = {} }) {
  const purchaseStatuses = order?.purchase_statuses || {};
  const addons = order?.additional_components || [];

  // Extract all components with dynamic qty_needed & stock resolution matching ProductionDetail
  const runningUsage = {};

  const processComponent = (comp, prodTargetQty, prodBotolSize, racikanList) => {
    const invId = comp.inventory_item_id;
    const namaKey = (comp.item_name || comp.name || '').toLowerCase().trim();
    const cleanKey = namaKey.replace(/\s+/g, ' ');

    let needed = Number(comp.qty_needed) || 0;
    if (needed === 0) {
      if (['botol', 'tutup', 'spray', 'box', 'tambahan'].includes(comp.category)) {
        needed = prodTargetQty || Number(order.target_qty) || Number(order.final_qty) || 1;
      } else if (['bibit', 'bahan_cair', 'dpg', 'alkohol'].includes(comp.category) || (comp.category || '').includes('bahan')) {
        const rMatch = racikanList.find(r => {
          const rNama = (r.nama_bahan || r.bahan_nama || r.nama || '').toLowerCase().trim();
          return rNama && (cleanKey.includes(rNama) || rNama.includes(cleanKey));
        });
        if (rMatch) {
          if (rMatch.kebutuhan_nilai && Number(rMatch.kebutuhan_nilai) > 0) {
            let val = Number(rMatch.kebutuhan_nilai);
            const sat = (rMatch.kebutuhan_satuan || rMatch.satuan || '').toLowerCase();
            const compUnit = (comp.unit || '').toLowerCase();
            if ((compUnit === 'l' || compUnit === 'liter' || compUnit === 'kg') && (sat === 'ml' || sat === 'g' || sat === 'gr')) {
              needed = val / 1000;
            } else if ((compUnit === 'ml' || compUnit === 'g' || compUnit === 'gr') && (sat === 'l' || sat === 'liter' || sat === 'kg')) {
              needed = val * 1000;
            } else {
              needed = val;
            }
          } else if (rMatch.persentase) {
            const pct = parseFloat(String(rMatch.persentase).replace(',', '.')) || 0;
            const totalVolMl = prodTargetQty * (prodBotolSize || 10);
            const mlNeeded = (pct / 100) * totalVolMl;
            const compUnit = (comp.unit || '').toLowerCase();
            if (compUnit === 'l' || compUnit === 'liter' || compUnit === 'kg') {
              needed = mlNeeded / 1000;
            } else {
              needed = mlNeeded;
            }
          }
        }
      }
    }

    const usageKey = invId || cleanKey || 'unknown';
    const priorUsage = runningUsage[usageKey] || 0;

    const rawStock = (invId && stockMap[invId] !== undefined)
      ? stockMap[invId]
      : (cleanKey && stockMap[cleanKey] !== undefined)
        ? stockMap[cleanKey]
        : (comp.stock_available ?? 0);

    const realStock = Math.max(0, rawStock - priorUsage);
    if (needed > 0) {
      runningUsage[usageKey] = priorUsage + needed;
    }

    const isFromClient = comp.vendor === 'Client' || comp.dari_client || ['pending_client', 'ready_client'].includes(comp.status);

    let status = comp.status || 'pending';
    if (comp.status === 'pending_client') {
      status = 'pending_client';
    } else if (comp.status === 'ready_client') {
      status = 'ready_client';
    } else if (needed > 0 && realStock >= needed) {
      status = isFromClient ? 'ready_client' : 'ready';
    } else if (needed > 0 && realStock < needed) {
      status = isFromClient ? 'pending_client' : 'need_buy';
    } else if (realStock > 0) {
      status = isFromClient ? 'ready_client' : 'ready';
    } else {
      status = isFromClient ? 'pending_client' : 'need_buy';
    }

    return { ...comp, stock_available: realStock, status, qty_needed: needed };
  };

  const linkedComps = [];
  if (order?.products && order.products.length > 0) {
    order.products.forEach(p => {
      const pTarget = Number(p.target_qty || p.qty || 0);
      const pSize = Number(p.botol_ukuran_aktual || p.botol_ukuran_label || p.ukuran_botol_ml || 10);
      const pRacikan = p.racikan_digunakan || p.racikan || order.racikan_digunakan || [];
      const pComps = (p.components && p.components.length > 0) ? p.components : (order.components || []);
      pComps.forEach(c => {
        if (c.inventory_item_id || ['pending_client', 'ready_client'].includes(c.status) || c.item_name || c.name) {
          linkedComps.push(processComponent(c, pTarget, pSize, pRacikan));
        }
      });
    });
  } else if (order?.components && order.components.length > 0) {
    const pTarget = Number(order.target_qty || order.final_qty || 0);
    const pSize = Number(order.ukuran_botol_ml || 10);
    const pRacikan = order.racikan_digunakan || [];
    order.components.forEach(c => {
      if (c.inventory_item_id || ['pending_client', 'ready_client'].includes(c.status) || c.item_name || c.name) {
        linkedComps.push(processComponent(c, pTarget, pSize, pRacikan));
      }
    });
  }

  const STAGES_PAST_RACIK = ['in_progress', 'diracik', 'racik', 'mixing', 'maserasi', 'menunggu_filling', 'filling', 'menunggu_packing', 'packing', 'siap_kirim', 'done', 'selesai'];
  const STAGES_PAST_FILLING = ['menunggu_packing', 'packing', 'siap_kirim', 'done', 'selesai'];
  const isCategoryConsumed = (category, poStatus) => {
    const cat = String(category || '').toLowerCase().trim();
    if (['bibit', 'bahan_cair', 'bahan cair', 'bahan', 'pelarut', 'alkohol', 'campuran', 'parfum', 'fragrance', 'dpg'].some(c => cat === c || cat.includes(c))) return STAGES_PAST_RACIK.includes(poStatus);
    if (['botol', 'tutup', 'spray', 'fullset', 'packaging', 'box', 'kemasan', 'label', 'stiker'].some(c => cat === c || cat.includes(c))) return STAGES_PAST_FILLING.includes(poStatus);
    return false;
  };

  const getEffectiveStatus = (comp) => {
    if (isCategoryConsumed(comp.category, order.status)) return 'ready';
    return comp.status;
  };

  const readyCount = linkedComps.filter(c => getEffectiveStatus(c) === 'ready' || getEffectiveStatus(c) === 'ready_client').length;
  const needBuyCount = linkedComps.filter(c => getEffectiveStatus(c) === 'need_buy').length;

  const addonTotal = addons.length;
  const addonReady = addons.filter(a => a.status === 'sudah_diterima').length;
  const totalItems = linkedComps.length + addonTotal;
  const totalReady = readyCount + addonReady;
  
  const isRunning = !['pending', 'sedang_po_item', 'antrian_produksi'].includes(order.status);
  const stagePercentages = {
    pending: 0, sedang_po_item: 0, antrian_produksi: 0,
    in_progress: 10, diracik: 20, maserasi: 40,
    menunggu_filling: 50, filling: 60, menunggu_packing: 70,
    packing: 80, siap_kirim: 90, dalam_pengiriman: 95, shipping: 95, done: 100
  };

  let percentage = 0;
  if (isRunning) {
    percentage = stagePercentages[order.status] || 10;
  } else {
    percentage = totalItems > 0 ? Math.round((totalReady / totalItems) * 100) : 0;
  }

  const isItemPOedInPurchasing = (comp) => {
    const invId = comp.inventory_item_id;
    const nameKey = (comp.item_name || comp.name || '').toLowerCase().trim();
    const cleanKey = nameKey.replace(/\s+/g, ' ');
    const purchaseStatuses = order?.purchase_statuses || {};

    if (invId && purchaseStatuses[invId] && !['need_buy', 'pending', 'kurang'].includes(purchaseStatuses[invId])) {
      return true;
    }
    if (cleanKey && purchaseStatuses[cleanKey] && !['need_buy', 'pending', 'kurang'].includes(purchaseStatuses[cleanKey])) {
      return true;
    }
    return false;
  };

  const displayStatus = (() => {
    if (!order) return 'pending';
    const isPastPrep = !['pending', 'sedang_po_item', 'antrian_produksi'].includes(order.status);
    if (isPastPrep) return order.status;

    if (percentage === 100) return 'antrian_produksi';

    const needBuyComps = linkedComps.filter(c => getEffectiveStatus(c) === 'need_buy');
    const isAnyPOed = needBuyComps.length > 0 && needBuyComps.some(c => isItemPOedInPurchasing(c));
    if (isAnyPOed) return 'sedang_po_item';

    return 'pending';
  })();

  React.useEffect(() => {
    // Auto-sync status to database so Marketing/Finance also sees the updated status
    if (order?.id && ['pending', 'sedang_po_item', 'antrian_produksi'].includes(order.status) && displayStatus !== order.status) {
      import('@/api/base44Client').then(({ base44 }) => {
        base44.entities.ProductionOrder.update(order.id, { status: displayStatus })
          .catch(err => console.error('Auto-update PO status failed:', err));
      });
    }
  }, [displayStatus, order?.status, order?.id]);

  const PRIMER_STATUSES = ['in_progress', 'diracik', 'menunggu_qc_mixing', 'maserasi', 'menunggu_filling', 'filling', 'menunggu_qc_filling'];
  const SEKUNDER_STATUSES = ['menunggu_packing', 'packing', 'waiting_filling', 'menunggu_qc_packing'];

  const isPrimer = isRunning && PRIMER_STATUSES.includes(order.status);
  const isSekunder = isRunning && SEKUNDER_STATUSES.includes(order.status);

  // Custom Elegant Stepper Component
  const renderStepper = () => {
    const steps = [
      { key: 'racik', label: 'Racik', triggers: ['in_progress', 'diracik', 'menunggu_qc_mixing'] },
      { key: 'maserasi', label: 'Maserasi', triggers: ['maserasi'] },
      { key: 'filling', label: 'Filling', triggers: ['filling', 'menunggu_qc_filling'] },
      { key: 'packing', label: 'Pengemasan', triggers: ['packing', 'waiting_filling', 'menunggu_qc_packing'] },
      { key: 'siap_kirim', label: 'Siap Kirim', triggers: ['siap_kirim'] },
      { key: 'selesai', label: 'Selesai', triggers: ['dalam_pengiriman', 'shipping', 'done', 'is_shipped'] },
    ];

    let currentIndex = 0;
    if (order.status === 'menunggu_qc_mixing') {
      currentIndex = 0.5;
    } else if (order.status === 'menunggu_filling') {
      currentIndex = 1.5;
    } else if (order.status === 'menunggu_qc_filling') {
      currentIndex = 2.4;
    } else if (order.status === 'menunggu_packing') {
      currentIndex = 2.6;
    } else if (order.status === 'menunggu_qc_packing' || order.status === 'waiting_filling') {
      currentIndex = 3.5;
    } else if (['dalam_pengiriman', 'shipping'].includes(order.status)) {
      currentIndex = 4.5;
    } else {
      for (let i = 0; i < steps.length; i++) {
        if (steps[i].triggers.includes(order.status)) {
          currentIndex = i;
          break;
        }
      }
    }

    const totalSteps = steps.length - 1; // 5 intervals

    return (
      <div className="w-full mt-2 md:mt-4 mb-3 md:mb-6">
        <div className="relative flex items-center justify-between w-full px-1 md:px-2">
          {/* Background Line */}
          <div className="absolute top-1/2 left-3 md:left-4 right-3 md:right-4 h-[1px] md:h-[2px] bg-slate-100 -translate-y-1/2 z-0 rounded-full" />
          
          {/* Active Line */}
          <div 
            className="absolute top-1/2 left-3 md:left-4 h-[1px] md:h-[2px] bg-gradient-to-r from-[#D4AF37] to-[#8C6208] -translate-y-1/2 z-0 transition-all duration-700 ease-in-out rounded-full" 
            style={{ width: `calc(${(currentIndex / totalSteps) * 100}% - 12px)` }} 
          />

          {/* Floating Intermediary Node: Menunggu QC - Mixing */}
          {order.status === 'menunggu_qc_mixing' && (
            <div className="absolute top-1/2 left-[10%] -translate-x-1/2 -translate-y-1/2 z-20 flex flex-col items-center">
               <div className="w-3.5 h-3.5 md:w-5 md:h-5 rounded-full flex items-center justify-center bg-white border-2 md:border-[3px] border-purple-600 shadow-[0_0_8px_rgba(147,51,234,0.3)]">
                  <div className="w-1 h-1 md:w-1.5 md:h-1.5 rounded-full bg-purple-600 animate-pulse" />
               </div>
               <div className="absolute -top-5 md:-top-6 text-[6px] md:text-[8px] font-bold text-purple-800 tracking-wide whitespace-nowrap bg-purple-50 px-1.5 md:px-2 py-0.5 rounded-full border border-purple-200 shadow-xs">
                  Menunggu QC Mixing
               </div>
            </div>
          )}

          {/* Floating Intermediary Node: Menunggu Filling */}
          {order.status === 'menunggu_filling' && (
            <div className="absolute top-1/2 left-[30%] -translate-x-1/2 -translate-y-1/2 z-20 flex flex-col items-center">
               <div className="w-3.5 h-3.5 md:w-5 md:h-5 rounded-full flex items-center justify-center bg-white border-2 md:border-[3px] border-[#8C6208] shadow-[0_0_8px_rgba(140,98,8,0.2)]">
                  <div className="w-1 h-1 md:w-1.5 md:h-1.5 rounded-full bg-[#8C6208] animate-pulse" />
               </div>
               <div className="absolute -top-5 md:-top-6 text-[6px] md:text-[8px] font-semibold text-amber-800 tracking-wide whitespace-nowrap bg-[#FFFDF3] px-1.5 md:px-2 py-0.5 rounded-full border border-[#F6E9CC] shadow-xs">
                  Menunggu Filling
               </div>
            </div>
          )}

          {/* Floating Intermediary Node: Menunggu QC - Filling */}
          {order.status === 'menunggu_qc_filling' && (
            <div className="absolute top-1/2 left-[48%] -translate-x-1/2 -translate-y-1/2 z-20 flex flex-col items-center">
               <div className="w-3.5 h-3.5 md:w-5 md:h-5 rounded-full flex items-center justify-center bg-white border-2 md:border-[3px] border-indigo-600 shadow-[0_0_8px_rgba(79,70,229,0.3)]">
                  <div className="w-1 h-1 md:w-1.5 md:h-1.5 rounded-full bg-indigo-600 animate-pulse" />
               </div>
               <div className="absolute -top-5 md:-top-6 text-[6px] md:text-[8px] font-bold text-indigo-800 tracking-wide whitespace-nowrap bg-indigo-50 px-1.5 md:px-2 py-0.5 rounded-full border border-indigo-200 shadow-xs">
                  Menunggu QC Filling
               </div>
            </div>
          )}

          {/* Floating Intermediary Node: Menunggu Packing */}
          {order.status === 'menunggu_packing' && (
            <div className="absolute top-1/2 left-[52%] -translate-x-1/2 -translate-y-1/2 z-20 flex flex-col items-center">
               <div className="w-3.5 h-3.5 md:w-5 md:h-5 rounded-full flex items-center justify-center bg-white border-2 md:border-[3px] border-[#8C6208] shadow-[0_0_8px_rgba(140,98,8,0.2)]">
                  <div className="w-1 h-1 md:w-1.5 md:h-1.5 rounded-full bg-[#8C6208] animate-pulse" />
               </div>
               <div className="absolute -top-5 md:-top-6 text-[6px] md:text-[8px] font-semibold text-amber-800 tracking-wide whitespace-nowrap bg-[#FFFDF3] px-1.5 md:px-2 py-0.5 rounded-full border border-[#F6E9CC] shadow-xs">
                  Menunggu Pengemasan
               </div>
            </div>
          )}

          {/* Floating Intermediary Node: Menunggu QC - Pengemasan */}
          {(order.status === 'menunggu_qc_packing' || order.status === 'waiting_filling') && (
            <div className="absolute top-1/2 left-[70%] -translate-x-1/2 -translate-y-1/2 z-20 flex flex-col items-center">
               <div className="w-3.5 h-3.5 md:w-5 md:h-5 rounded-full flex items-center justify-center bg-white border-2 md:border-[3px] border-purple-600 shadow-[0_0_8px_rgba(147,51,234,0.3)]">
                  <div className="w-1 h-1 md:w-1.5 md:h-1.5 rounded-full bg-purple-600 animate-pulse" />
               </div>
               <div className="absolute -top-5 md:-top-6 text-[6px] md:text-[8px] font-bold text-purple-800 tracking-wide whitespace-nowrap bg-purple-50 px-1.5 md:px-2 py-0.5 rounded-full border border-purple-200 shadow-xs">
                  {order.status === 'waiting_filling' ? 'Pengemasan OK' : 'Menunggu QC Pengemasan'}
               </div>
            </div>
          )}

          {/* Nodes */}
          {steps.map((step, idx) => {
            const isPast = idx < currentIndex;
            const isActive = Math.floor(currentIndex) === idx || idx === currentIndex;
            return (
              <div key={step.key} className="relative z-10 flex flex-col items-center">
                 <div className={`w-4 h-4 md:w-5 md:h-5 rounded-full flex items-center justify-center transition-all duration-500 shadow-sm
                    ${isPast ? 'bg-[#8C6208] border-none' : isActive ? 'bg-white border-[2px] md:border-[3px] border-[#8C6208] shadow-[0_0_8px_rgba(140,98,8,0.2)]' : 'bg-white border-2 md:border-2 border-slate-200'}`}>
                   {isPast && <svg className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                   {isActive && (
                     step.key === 'siap_kirim' ? (
                       <div className="w-[5px] h-[5px] md:w-1.5 md:h-1.5 rounded-full bg-teal-600" />
                     ) : step.key === 'selesai' && ['dalam_pengiriman', 'shipping'].includes(order.status) ? (
                       <div className="w-[5px] h-[5px] md:w-1.5 md:h-1.5 rounded-full bg-blue-600 animate-pulse" />
                     ) : (
                       <div className="w-[5px] h-[5px] md:w-1.5 md:h-1.5 rounded-full bg-[#8C6208]" />
                     )
                   )}
                 </div>
                 <span className={`absolute top-5 md:top-6 text-[7px] md:text-[8.5px] font-bold tracking-tight md:tracking-wide transition-colors duration-300 whitespace-nowrap ${isActive ? 'text-[#8C6208]' : isPast ? 'text-slate-700' : 'text-slate-300'}`}>{step.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Link to={`/office/production/${order.id}`}>
      <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer border-white/60 overflow-hidden bg-white/80  rounded-xl md:rounded-[2rem]">
        <CardContent className="p-4 md:p-6">
          <div className="flex justify-between items-start mb-1 md:mb-4">
              <div className="flex-1 min-w-0 pr-1 md:pr-4">
               <div className="flex items-center gap-1.5 md:gap-2 mb-0.5 md:mb-1.5 flex-wrap shrink-0 overflow-hidden">
                  <span className="text-[10px] md:text-xs text-slate-400 font-mono font-bold truncate">#{order.order_number}</span>
                  <StatusBadge status={displayStatus} small={true} />
                  {isPrimer && (
                    <span className="text-[8px] md:text-[9px] font-semibold px-2 py-0.5 rounded-full bg-stone-100 text-stone-600 border border-stone-200/70">
                      Primer
                    </span>
                  )}
                  {isSekunder && (
                    <span className="text-[8px] md:text-[9px] font-semibold px-2 py-0.5 rounded-full bg-stone-100 text-stone-600 border border-stone-200/70">
                      Sekunder
                    </span>
                  )}
                  {order.is_returned_po && (
                    <span className="bg-rose-100 text-rose-700 border border-rose-200 px-2 py-0.5 rounded-full text-[9px] md:text-[10px] font-bold flex items-center gap-1 shadow-sm">
                      <RotateCcw className="w-2.5 h-2.5" /> Hasil Return
                    </span>
                  )}
                  {order.status === 'maserasi' && <MaserasiTimer order={order} small={true} />}
               </div>
               <h3 className="font-display text-lg md:text-lg font-black tracking-tight text-[#302621] truncate leading-tight mt-1">{order.brand_name}</h3>
               {order.product_name && <p className="text-[10px] md:text-xs font-medium text-slate-500 truncate mt-0">{order.product_name}</p>}
               {order.created_date && <p className="text-[9px] md:text-[10px] text-slate-400 mt-1 font-medium">Tanggal Diinput: <span className="text-slate-600 font-bold">{new Date(order.created_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}</span></p>}
             </div>
             <ChevronRight className="w-4 h-4 md:w-5 md:h-5 text-slate-300 group-hover:text-[#8C6208] transition-colors shrink-0" />
          </div>
          {isRunning ? renderStepper() : (
            <div className="flex items-center gap-2 bg-slate-50/50 p-2 rounded-xl border border-slate-100/60">
              <ProgressRing percentage={percentage} size={24} strokeWidth={3} />
              <div className="flex-1"><p className="text-[7px] font-bold text-slate-600">Persiapan Item</p></div>
            </div>
          )}
          <div className="mt-4 md:mt-8 pt-3 md:pt-4 border-t border-slate-100 flex items-center justify-between">
             <div className="flex items-center gap-4 md:gap-4">
                 <div className="flex flex-col gap-1">
                   <span className="text-[9px] md:text-[9px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1 md:gap-1"><Clock className="w-3 h-3 md:w-3 md:h-3" /> Deadline</span>
                   {order.deadline ? <span className="text-xs md:text-xs font-bold text-slate-700 whitespace-nowrap">{new Date(order.deadline).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })}</span> : <span className="text-[10px] md:text-xs font-medium text-slate-400 italic">Belum diatur</span>}
                 </div>
                 {order.variants_count > 1 && (
                   <div className="flex flex-col gap-1">
                     <span className="text-[9px] md:text-[9px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1 md:gap-1"><Layers className="w-3 h-3 md:w-3 md:h-3" /> Varian</span>
                     <span className="text-xs md:text-xs font-bold text-slate-700 whitespace-nowrap">{order.variants_count} Tipe</span>
                   </div>
                 )}
             </div>
             <div className="text-right flex flex-col items-end gap-1">
                <span className="text-[9px] md:text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Update</span>
                <div className="scale-90 md:scale-90 origin-right whitespace-nowrap"><LastUpdated date={order.updated_date || order.created_date} /></div>
             </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}